import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLocationTracking } from '@/hooks/useLocationTracking';

export interface LoginActivity {
  id: string;
  user_id: string;
  timestamp: string;
  device_info: string;
  location: string;
  ip_address: string;
  user_agent: string;
  status: 'success' | 'failed' | 'blocked' | 'current';
  session_id?: string;
}

interface LoginActivitySummary {
  id: string;
  date: string;
  device: string;
  location: string;
  status: string;
}

// Detect device and browser information
const getDeviceInfo = (): string => {
  const userAgent = navigator.userAgent;
  let browser = 'Unknown Browser';
  let os = 'Unknown OS';

  // Detect browser
  if (userAgent.includes('Chrome') && !userAgent.includes('Edg')) {
    browser = 'Chrome';
  } else if (userAgent.includes('Firefox')) {
    browser = 'Firefox';
  } else if (userAgent.includes('Safari') && !userAgent.includes('Chrome')) {
    browser = 'Safari';
  } else if (userAgent.includes('Edg')) {
    browser = 'Edge';
  }

  // Detect OS
  if (userAgent.includes('Windows')) {
    os = 'Windows';
  } else if (userAgent.includes('Mac')) {
    os = 'MacOS';
  } else if (userAgent.includes('Linux')) {
    os = 'Linux';
  } else if (userAgent.includes('iPhone') || userAgent.includes('iPad')) {
    os = 'iOS';
  } else if (userAgent.includes('Android')) {
    os = 'Android';
  }

  return `${browser} on ${os}`;
};

// This will be replaced with the hook inside the component

export const useLoginActivity = () => {
  const [recentActivity, setRecentActivity] = useState<LoginActivitySummary[]>([]);
  const [allActivity, setAllActivity] = useState<LoginActivitySummary[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  // Use location tracking hook
  const { getLocationForLoginActivity } = useLocationTracking();

  // Record a new login attempt
  const recordLoginAttempt = async (status: 'success' | 'failed' | 'blocked') => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const deviceInfo = getDeviceInfo();
      const location = await getLocationForLoginActivity();
      const timestamp = new Date().toISOString();
      const userAgent = navigator.userAgent;
      const ipAddress = 'XXX.XXX.XXX.XXX'; // In production, get from server

      const loginRecord: Partial<LoginActivity> = {
        user_id: user.id,
        timestamp,
        device_info: deviceInfo,
        location,
        ip_address: ipAddress,
        user_agent: userAgent,
        status,
        session_id: status === 'success' ? crypto.randomUUID() : undefined
      };

      // Store in Supabase (simulated for now)
      console.log('Recording login attempt:', loginRecord);
      
      // In production, you'd have a login_activity table:
      // const { error } = await supabase.from('login_activity').insert([loginRecord]);
      
      // For now, store in localStorage for immediate feedback
      const existingActivity = JSON.parse(localStorage.getItem('loginActivity') || '[]');
      const newActivity = {
        id: crypto.randomUUID(),
        date: new Date(timestamp).toLocaleString(),
        device: deviceInfo,
        location,
        status: status === 'success' ? 'Current session' : status.charAt(0).toUpperCase() + status.slice(1)
      };
      
      const updatedActivity = [newActivity, ...existingActivity].slice(0, 50); // Keep last 50
      localStorage.setItem('loginActivity', JSON.stringify(updatedActivity));
      
      // Update state
      await fetchLoginActivity();
      
    } catch (error) {
      console.error('Error recording login attempt:', error);
    }
  };

  // Fetch login activity from storage/database
  const fetchLoginActivity = async () => {
    try {
      setIsLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsLoading(false);
        return;
      }

      // In production, fetch from Supabase:
      // const { data, error } = await supabase
      //   .from('login_activity')
      //   .select('*')
      //   .eq('user_id', user.id)
      //   .order('timestamp', { ascending: false })
      //   .limit(50);

      // For now, get from localStorage with some default data
      let storedActivity = JSON.parse(localStorage.getItem('loginActivity') || '[]');
      
      // If no stored activity, create some default entries
      if (storedActivity.length === 0) {
        const defaultActivity = [
          {
            id: '1',
            date: new Date().toLocaleString(),
            device: getDeviceInfo(),
            location: await getLocationForLoginActivity(),
            status: 'Current session'
          },
          {
            id: '2',
            date: new Date(Date.now() - 24 * 60 * 60 * 1000).toLocaleString(),
            device: 'Mobile App on iOS',
            location: 'Boston, USA',
            status: 'Success'
          },
          {
            id: '3',
            date: new Date(Date.now() - 3 * 24 * 60 * 60 * 1000).toLocaleString(),
            device: 'Firefox on Windows',
            location: 'Chicago, USA',
            status: 'Success'
          },
          {
            id: '4',
            date: new Date(Date.now() - 5 * 24 * 60 * 60 * 1000).toLocaleString(),
            device: 'Mobile App on Android',
            location: 'Unknown location',
            status: 'Blocked'
          }
        ];
        
        localStorage.setItem('loginActivity', JSON.stringify(defaultActivity));
        storedActivity = defaultActivity;
      }

      setAllActivity(storedActivity);
      setRecentActivity(storedActivity.slice(0, 4)); // Show only last 4
      setError(null);
      
    } catch (err) {
      console.error('Error fetching login activity:', err);
      setError('Failed to load login activity');
    } finally {
      setIsLoading(false);
    }
  };

  // Update current session status
  const markCurrentSession = async () => {
    try {
      const deviceInfo = getDeviceInfo();
      const location = await getLocationForLoginActivity();
      
      const existingActivity = JSON.parse(localStorage.getItem('loginActivity') || '[]');
      
      // Remove any existing "Current session" entries
      const filteredActivity = existingActivity.filter((activity: LoginActivitySummary) => 
        activity.status !== 'Current session'
      );
      
      // Add new current session at the top
      const currentSession = {
        id: crypto.randomUUID(),
        date: new Date().toLocaleString(),
        device: deviceInfo,
        location,
        status: 'Current session'
      };
      
      const updatedActivity = [currentSession, ...filteredActivity].slice(0, 50);
      localStorage.setItem('loginActivity', JSON.stringify(updatedActivity));
      
      await fetchLoginActivity();
      
    } catch (error) {
      console.error('Error marking current session:', error);
    }
  };

  // Initialize and mark current session
  useEffect(() => {
    const initializeActivity = async () => {
      await markCurrentSession();
      await fetchLoginActivity();
    };
    
    initializeActivity();
  }, []);

  return {
    recentActivity,
    allActivity,
    isLoading,
    error,
    recordLoginAttempt,
    fetchLoginActivity,
    markCurrentSession
  };
}; 